import {
  Component,
  ViewEncapsulation,
  Input,
  AfterViewInit,
  ContentChildren,
  QueryList,
  ViewChild,
  ElementRef,
  OnDestroy,
  Renderer2,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ITab } from './tab.model';
import { WlxTabComponent } from './tab.component';
import { TabsController } from './tabs.service';
import { WlxTabViewComponent } from './tab-view.component';

@Component({
  standalone: true,
  selector: 'wlx-tabs',
  templateUrl: 'tabs.component.html',
  styleUrls: ['vertical-tabs.component.less'],
  encapsulation: ViewEncapsulation.None,
  providers: [TabsController],
  imports: [CommonModule, WlxTabViewComponent],
  host: {
    '(document:click)': 'onDocumentClick($event)',
  },
})
export class WlxTabsComponent implements AfterViewInit, OnDestroy {
  @Input() public windowed: boolean = false;
  public isOnFirstTab: boolean = false;
  public isOnLastTab: boolean = false;
  @Input() public tabAffix: boolean = false;
  @Input() public tabAffixOffset: number = 0;
  @Input() public collapseSubTabs = false;

  @ContentChildren(WlxTabComponent, { descendants: false }) public childTabs: QueryList<ITab>;

  @ViewChild('tabToggle', { static: false }) public tabToggleElement: ElementRef;
  @ViewChild('tabDropdownOuterContainer', { static: false }) public tabDropdownOuterContainerElement: ElementRef;
  @ViewChild('tabDropdownContainer', { static: false }) public tabDropdownContainerElement: ElementRef;

  public allTabs: ITab[] = [];
  public showTabNav: boolean = false;
  public _tabChangeSubscription: any;
  public _enableChangeSubscription: any;

  public constructor(
    public _tabsCtrl: TabsController,
    private renderer: Renderer2
  ) {
    this._tabChangeSubscription = this._tabsCtrl.tabChange.subscribe(() => {
      this.isOnFirstTab = this._tabsCtrl.isOnFirstTab;
      this.isOnLastTab = this._tabsCtrl.isOnLastTab;
    });

    this._enableChangeSubscription = this._tabsCtrl.tabEnableChange.subscribe(() => {
      this.isOnFirstTab = this._tabsCtrl.isOnFirstTab;
      this.isOnLastTab = this._tabsCtrl.isOnLastTab;
    });
  }

  public ngOnDestroy(): void {
    if (this._tabChangeSubscription?.unsubscribe) this._tabChangeSubscription.unsubscribe();
    if (this._enableChangeSubscription?.unsubscribe) this._enableChangeSubscription.unsubscribe();
  }

  public selectFirst() {
    this.select(this.allTabs[0]);
  }

  public select(tab: ITab): void {
    this._tabsCtrl.select(tab);
    this.isOnFirstTab = this._tabsCtrl.isOnFirstTab;
    this.isOnLastTab = this._tabsCtrl.isOnLastTab;
  }

  public addTab(tab: ITab): void {
    this._tabsCtrl.addTab(tab);
  }

  public ngAfterViewInit(): void {
    setTimeout(() => {
      this.allTabs = this._tabsCtrl.getAllTabs();
      let enabledChildTags = this.getEnabledChildTags();
      var selectedTab = enabledChildTags.filter((x) => x.selected);

      if (selectedTab.length) this._tabsCtrl.select(selectedTab[0]);
      else if (enabledChildTags.length) this._tabsCtrl.select(enabledChildTags[0]);
    });

    if (this.tabAffix) {
      this.setupAffix();
    }
  }

  private refreshTabs(): void {
    setTimeout(() => {
      this.allTabs = this.childTabs.toArray();
      let enabledChildTags = this.getEnabledChildTags();

      if (!enabledChildTags.some((t) => t.selected) && enabledChildTags.length) {
        this.select(enabledChildTags[0]);
      }
    });
  }

  private setupAffix(): void {
    const elements = [this.tabToggleElement?.nativeElement, this.tabDropdownOuterContainerElement?.nativeElement];

    elements.forEach((el) => {
      if (el) {
        this.renderer.setStyle(el, 'position', 'sticky');
        this.renderer.setStyle(el, 'top', `${this.tabAffixOffset}px`);
        this.renderer.setStyle(el, 'z-index', '1000');
      }
    });
  }

  public getEnabledChildTags(): ITab[] {
    if (!this.childTabs) return [];
    return this.childTabs.filter((x) => x.enabled);
  }

  public selectNextTab(): void {
    let allTabs: ITab[] = this._tabsCtrl.getAllTabsAndSubsInOrder().filter((x) => x.enabled);
    if (allTabs.length == 0) return;
    let selectedTabs: ITab[] = allTabs.filter((x) => x.selected);
    let selectedTab = selectedTabs.length > 0 ? selectedTabs[selectedTabs.length - 1] : null;

    if (!selectedTab) {
      selectedTab = allTabs[0];
      this.select(selectedTab);
    }
    if (allTabs.length === 1) return;
    let nextIndex = allTabs.indexOf(selectedTab) + 1;
    if (nextIndex < allTabs.length) this.select(allTabs[nextIndex]);
  }

  public selectPreviousTab(): void {
    let allTabs: ITab[] = this._tabsCtrl.getAllTabsAndSubsInOrder().filter((x) => x.enabled);
    if (allTabs.length == 0) return;
    let selectedTabs: ITab[] = allTabs.filter((x) => x.selected);
    let selectedTab = selectedTabs.length > 0 ? selectedTabs[selectedTabs.length - 1] : null;

    if (!selectedTab) {
      selectedTab = allTabs[0];
      this.select(selectedTab);
    }
    if (allTabs.length === 1) return;
    let nextIndex = allTabs.indexOf(selectedTab) - 1;
    if (nextIndex >= 0) this.select(allTabs[nextIndex]);
  }

  public tabNavToggleClicked(): void {
    this.showTabNav = !this.showTabNav;
  }

  public onDocumentClick($event: any): void {
    if (!this.showTabNav) return;
    if ($event.target.parentNode == this.tabToggleElement.nativeElement || $event.target == this.tabToggleElement.nativeElement) return;
    this.showTabNav = false;
  }
}
